'use client'
import { createAlert } from '@gluestack-ui/core/alert/creator'
import { View, Text } from 'react-native'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { withStyleContext, useStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'
import { cssInterop } from 'nativewind'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { PrimitiveIcon } from '@gluestack-ui/core/icon/creator'

import { UIIcon } from '../icon/UIIcon'

const SCOPE = 'ALERT'

// AOS redesign: the gluestack defaults referenced unmapped tokens
// (`bg-background-error`, `text-error-800`, `border-outline-100`) so alerts
// rendered with no colour. Remap to AOS status tokens — `outline` is a soft
// tinted banner (status-muted bg + status border + status icon + light text),
// `solid` is a filled status banner (status bg + dark icon/text).
const alertStyle = tva({
    base: 'items-center py-3 px-4 rounded-md flex-row gap-2',

    variants: {
        action: {
            error: 'bg-status-muted-critical border-status-critical',
            warning: 'bg-status-muted-warning border-status-warning',
            success: 'bg-status-muted-success border-status-success',
            info: 'bg-status-muted-info border-status-info',
            muted: 'bg-status-muted-neutral border-status-neutral',
        },

        variant: {
            solid: '',
            outline: 'border',
        },
    },
    compoundVariants: [
        { action: 'error', variant: 'solid', class: 'bg-status-critical' },
        { action: 'warning', variant: 'solid', class: 'bg-status-warning' },
        { action: 'success', variant: 'solid', class: 'bg-status-success' },
        { action: 'info', variant: 'solid', class: 'bg-status-info' },
        { action: 'muted', variant: 'solid', class: 'bg-status-neutral' },
    ],
})

const alertTextStyle = tva({
    base: 'font-normal font-body',

    variants: {
        isTruncated: {
            true: 'web:truncate',
        },
        bold: {
            true: 'font-bold',
        },
        underline: {
            true: 'underline',
        },
        strikeThrough: {
            true: 'line-through',
        },
        size: {
            '2xs': 'text-2xs',
            'xs': 'text-xs',
            'sm': 'text-sm',
            'md': 'text-base',
            'lg': 'text-lg',
            'xl': 'text-xl',
            '2xl': 'text-2xl',
            '3xl': 'text-3xl',
            '4xl': 'text-4xl',
            '5xl': 'text-5xl',
            '6xl': 'text-6xl',
        },
        sub: {
            true: 'text-xs',
        },
        italic: {
            true: 'italic',
        },
        highlight: {
            true: 'bg-yellow-500',
        },
    },
    parentVariants: {
        // Solid banners use dark on-graphics text (readable on the bright status
        // fills); soft/outline banners use the default light text on the tint.
        variant: {
            solid: 'text-aos-text-on-graphics',
            outline: 'text-aos-text-default',
        },
    },
})

const alertIconStyle = tva({
    base: 'fill-none',
    variants: {
        size: {
            '2xs': 'h-3 w-3',
            'xs': 'h-3.5 w-3.5',
            'sm': 'h-4 w-4',
            'md': 'h-[18px] w-[18px]',
            'lg': 'h-5 w-5',
            'xl': 'h-6 w-6',
        },
    },
    parentVariants: {
        // Outline: icon carries the status colour. (Order matters — `variant`
        // is listed after `action` so solid's on-graphics wins the merge.)
        action: {
            error: 'text-status-critical',
            warning: 'text-status-warning',
            success: 'text-status-success',
            info: 'text-status-info',
            muted: 'text-status-neutral',
        },
        variant: {
            solid: 'text-aos-text-on-graphics',
            outline: '',
        },
    },
})

export const UIAlert = createAlert({
    Root: withStyleContext(View, SCOPE),
    Text: Text,
    Icon: UIIcon,
})

cssInterop(PrimitiveIcon, {
    className: {
        target: 'style',
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: 'classNameColor',
            stroke: true,
        },
    },
})

type IAlertProps = Omit<React.ComponentPropsWithoutRef<typeof UIAlert>, 'context'> &
    VariantProps<typeof alertStyle>

const Alert = React.forwardRef<React.ComponentRef<typeof UIAlert>, IAlertProps>(function Alert(
    { className, variant = 'solid', action = 'muted', ...props },
    ref,
) {
    return (
        <UIAlert
            className={alertStyle({ action, variant, class: className })}
            context={{ variant, action }}
            ref={ref}
            {...props}
        />
    )
})

type IAlertTextProps = React.ComponentPropsWithoutRef<typeof UIAlert.Text> &
    VariantProps<typeof alertTextStyle>

const AlertText = React.forwardRef<React.ComponentRef<typeof UIAlert.Text>, IAlertTextProps>(
    function AlertText(
        {
            className,
            isTruncated,
            bold,
            underline,
            strikeThrough,
            size = 'md',
            sub,
            italic,
            highlight,
            ...props
        },
        ref,
    ) {
        const { variant: parentVariant } = useStyleContext(SCOPE)
        return (
            <UIAlert.Text
                className={alertTextStyle({
                    isTruncated,
                    bold,
                    underline,
                    strikeThrough,
                    size,
                    sub,
                    italic,
                    highlight,
                    class: className,
                    parentVariants: {
                        variant: parentVariant,
                    },
                })}
                {...props}
                ref={ref}
            />
        )
    },
)

type IAlertIconProps = React.ComponentPropsWithoutRef<typeof UIAlert.Icon> &
    VariantProps<typeof alertIconStyle> & {
        height?: number
        width?: number
    }

const AlertIcon = React.forwardRef<React.ComponentRef<typeof UIAlert.Icon>, IAlertIconProps>(
    function AlertIcon({ className, size = 'md', ...props }, ref) {
        const { action: parentAction, variant: parentVariant } = useStyleContext(SCOPE)

        if (typeof size === 'number') {
            return (
                <UIAlert.Icon
                    ref={ref}
                    {...props}
                    className={alertIconStyle({ class: className })}
                    size={size}
                />
            )
        } else if (
            (props.height !== undefined || props.width !== undefined) &&
            size === undefined
        ) {
            return (
                <UIAlert.Icon
                    ref={ref}
                    {...props}
                    className={alertIconStyle({ class: className })}
                />
            )
        }
        return (
            <UIAlert.Icon
                className={alertIconStyle({
                    parentVariants: {
                        action: parentAction,
                        variant: parentVariant,
                    },
                    size,
                    class: className,
                })}
                {...props}
                ref={ref}
            />
        )
    },
)

Alert.displayName = 'Alert'
AlertText.displayName = 'AlertText'
AlertIcon.displayName = 'AlertIcon'

export { Alert, AlertText, AlertIcon }
